import React, { useState, useEffect } from 'react';
import MoviePoster from '../viewer/MoviePoster';
import StreamingServiceIcon from '../streaming/StreamingServiceIcon';
import { loadArchivedCalendarWeeks, loadScheduledMovies } from '../../services/calendarService';

const ReviewsTab = ({ 
  allMovies = [],
  movieReviews = {},
  onMovieClick,
  getStreamingServiceById,
  loadCalendarData,
  calendarDataLoading = false
}) => {
  const [reviewsMode, setReviewsMode] = useState('reviews');
  const [selectedHallYear, setSelectedHallYear] = useState('all');
  const [archivedWeeks, setArchivedWeeks] = useState([]);
  const [scheduledMoviesData, setScheduledMoviesData] = useState({});
  const [availableYears, setAvailableYears] = useState([]);

  // Lazy load calendar data when ReviewsTab is accessed
  useEffect(() => {
    if (loadCalendarData) {
      console.log('📝 ReviewsTab mounted - triggering calendar data load');
      loadCalendarData();
    }
  }, [loadCalendarData]);

  // Load calendar data for year filtering
  useEffect(() => {
    const loadReviewsCalendarData = async () => {
      try {
        // Check if we have batch-loaded data from App.js first
        const batchArchivedWeeks = JSON.parse(localStorage.getItem('archivedWeeks') || '[]');
        const batchScheduledMovies = JSON.parse(localStorage.getItem('all_scheduled_movies') || '{}');
        
        if (batchArchivedWeeks.length > 0 || Object.keys(batchScheduledMovies).length > 0) {
          console.log('✅ Using batch-loaded calendar data for reviews');
          setArchivedWeeks(batchArchivedWeeks);
          setScheduledMoviesData(batchScheduledMovies);
          
          // Extract available years from archived weeks
          const years = [...new Set(batchArchivedWeeks.map(week => week.year || new Date(week.start_date).getFullYear()))];
          setAvailableYears(years.sort((a, b) => b - a));
          return;
        }
        
        // Fallback: Load data individually if batch loading hasn't happened yet
        console.log('⚠️ Batch data not available, loading calendar data individually for reviews');
        
        // Load archived weeks
        const weeks = await loadArchivedCalendarWeeks();
        setArchivedWeeks(weeks);
        
        // Load scheduled movies (including archived ones for past reviews)
        const scheduled = await loadScheduledMovies(true);
        setScheduledMoviesData(scheduled);
        
        // Extract available years from archived weeks
        const years = [...new Set(weeks.map(week => week.year || new Date(week.start_date).getFullYear()))];
        setAvailableYears(years.sort((a, b) => b - a));
        
        console.log('✅ Calendar data loaded individually for reviews:', weeks.length, 'archived weeks');
      } catch (error) {
        console.error('❌ Error loading calendar data for reviews:', error);
        // Final fallback to localStorage
        try {
          const fallbackWeeks = JSON.parse(localStorage.getItem('archivedWeeks') || '[]');
          const fallbackScheduled = JSON.parse(localStorage.getItem('all_scheduled_movies') || JSON.parse(localStorage.getItem('scheduledMovies') || '{}'));
          setArchivedWeeks(fallbackWeeks);
          setScheduledMoviesData(fallbackScheduled);
          
          const fallbackYears = [...new Set(fallbackWeeks.map(week => week.year || new Date(week.week[0]).getFullYear()))];
          setAvailableYears(fallbackYears.sort((a, b) => b - a));
        } catch (fallbackError) {
          console.error('❌ Error loading fallback calendar data:', fallbackError);
        }
      }
    };

    loadReviewsCalendarData();
  }, []);

  // Hall of Horrors calculations
  const getMovieReviewStats = (movieId, filterYear = null) => {
    // If filtering by year, first check if this movie was scheduled in calendar weeks from that year
    if (filterYear) {
      const targetYear = parseInt(filterYear);
      const movieIdStr = String(movieId);
      let movieWasScheduledInYear = false;
      
      // Check if this movie was scheduled in any week from the target year
      for (const week of archivedWeeks) {
        const weekYear = week.year || new Date(week.start_date).getFullYear();
        if (weekYear === targetYear) {
          // Check all dates in this week using Supabase data
          const weekDates = week.dates || [];
          for (const date of weekDates) {
            const dayMovies = scheduledMoviesData[date] || [];
            if (dayMovies.some(sm => String(sm.movie.id) === movieIdStr)) {
              movieWasScheduledInYear = true;
              break;
            }
          }
          
          if (movieWasScheduledInYear) break;
        }
      }
      
      if (!movieWasScheduledInYear) {
        return null; // Movie wasn't scheduled in this year
      }
    }
    
    let reviews = movieReviews[movieId] || [];
    
    if (reviews.length < 3) return null; // Need at least 3 reviews
    
    const totalOverall = reviews.reduce((sum, r) => sum + r.overallRating, 0);
    const totalScariness = reviews.reduce((sum, r) => sum + r.scarinessRating, 0);
    
    const avgOverall = totalOverall / reviews.length;
    const avgScariness = totalScariness / reviews.length;
    
    // Weighted score: base average + bonus for more reviews (max 1.0 bonus)
    const reviewBonus = Math.min((reviews.length - 3) * 0.1, 1.0);
    const weightedOverall = avgOverall + reviewBonus;
    const weightedScariness = avgScariness + reviewBonus;
    
    return {
      movieId,
      reviewCount: reviews.length,
      avgOverall: Math.round(avgOverall * 10) / 10,
      avgScariness: Math.round(avgScariness * 10) / 10,
      weightedOverall: Math.round(weightedOverall * 10) / 10,
      weightedScariness: Math.round(weightedScariness * 10) / 10
    };
  };

  const getHallOfInfamyMovies = (type, filterYear = null) => {
    // Helper function to find movie by ID in scheduled movies data
    const findMovieById = (movieId) => {
      // Convert movieId to string for consistent comparison
      const movieIdStr = String(movieId);
      
      // First try to find in allMovies (base movie data)
      const baseMovie = allMovies.find(m => String(m.id) === movieIdStr);
      if (baseMovie) {
        return baseMovie;
      }
      
      // If not found, search in scheduledMoviesData (including archived)
      for (const dateMovies of Object.values(scheduledMoviesData)) {
        for (const scheduledMovie of dateMovies) {
          if (scheduledMovie.movie && String(scheduledMovie.movie.id) === movieIdStr) {
            return scheduledMovie.movie;
          }
        }
      }
      
      return null;
    };
    
    // Get all unique movie IDs from reviews (this includes archived movies)
    // Keep as strings to maintain consistency with TMDB IDs
    const reviewedMovieIds = Object.keys(movieReviews);
    
    const movieStats = reviewedMovieIds
      .map(movieId => {
        const movie = findMovieById(movieId);
        const stats = getMovieReviewStats(movieId, filterYear);
        return movie && stats ? { ...stats, movie } : null;
      })
      .filter(stat => stat && stat.movieId) // Only movies with 3+ reviews and found movie data
      .sort((a, b) => {
        const scoreA = type === 'best' ? a.weightedOverall : a.weightedScariness;
        const scoreB = type === 'best' ? b.weightedOverall : b.weightedScariness;
        
        if (scoreB !== scoreA) {
          return scoreB - scoreA;
        }
        
        // Tie-breaker: more reviews wins
        return b.reviewCount - a.reviewCount;
      });
    
    return movieStats.slice(0, 5); // Top 5
  };

  // availableYears is now loaded via useEffect hook

  // Show loading state if calendar data is being loaded
  if (calendarDataLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400"></div>
        <h3 className="text-xl font-semibold text-gray-400">Loading Reviews Data...</h3>
        <p className="text-gray-500">Fetching movie reviews and calendar information...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex justify-center space-x-2 mb-6">
        <button
          onClick={() => setReviewsMode('reviews')}
          className={`px-6 py-3 rounded-md font-bold transition-colors ${
            reviewsMode === 'reviews'
              ? 'bg-purple-600 text-white'
              : 'text-orange-300 hover:text-orange-100 hover:bg-gray-700'
          }`}
        >
          📝 Reviews
        </button>
        <button
          onClick={() => setReviewsMode('hallOfInfamy')}
          className={`px-6 py-3 rounded-md font-bold transition-colors ${
            reviewsMode === 'hallOfInfamy'
              ? 'bg-purple-600 text-white'
              : 'text-orange-300 hover:text-orange-100 hover:bg-gray-700'
          }`}
        >
          🏆 Hall of Horrors
        </button>
      </div>


      {/* Reviews View */}
      {reviewsMode === 'reviews' && (
        <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-4 py-3 border-b-2 border-orange-500">
            <h2 className="text-xl font-bold text-orange-300 flex items-center gap-2">
              📝 Reviewed Movies
            </h2>
            <p className="text-orange-200 text-sm">
              Movies with viewer reviews and aggregated ratings
            </p>
          </div>
          
          <div className="p-6 max-h-[70vh] overflow-auto">
            {(() => {
              // Helper function to find movie by ID in scheduled movies data
              const findMovieById = (movieId) => {
                console.log('🔍 findMovieById: Looking for movieId:', movieId, 'type:', typeof movieId);
                
                // Convert to string for consistent comparison
                const movieIdStr = String(movieId);
                
                // First try to find in allMovies (base movie data) - compare as strings
                console.log('🔍 findMovieById: Searching in allMovies, count:', allMovies.length);
                console.log('🔍 findMovieById: Sample allMovies IDs:', allMovies.slice(0, 3).map(m => m.id));
                const baseMovie = allMovies.find(m => String(m.id) === movieIdStr);
                if (baseMovie) {
                  console.log('🔍 findMovieById: Found in allMovies:', baseMovie.title);
                  return baseMovie;
                }
                
                // If not found, search in scheduledMoviesData (including archived)
                console.log('🔍 findMovieById: Not found in allMovies, searching scheduledMoviesData...');
                console.log('🔍 findMovieById: scheduledMoviesData keys:', Object.keys(scheduledMoviesData));
                for (const dateMovies of Object.values(scheduledMoviesData)) {
                  for (const scheduledMovie of dateMovies) {
                    if (scheduledMovie.movie && String(scheduledMovie.movie.id) === movieIdStr) {
                      console.log('🔍 findMovieById: Found in scheduledMoviesData:', scheduledMovie.movie.title);
                      return scheduledMovie.movie;
                    }
                  }
                }
                
                console.log('🔍 findMovieById: Movie not found anywhere for ID:', movieId);
                return null;
              };
              
              // Get movies with reviews and calculate aggregates
              console.log('🔍 ReviewsTab: movieReviews prop:', movieReviews);
              console.log('🔍 ReviewsTab: Object.keys(movieReviews):', Object.keys(movieReviews));
              
              const reviewedMovies = [];
              Object.entries(movieReviews).forEach(([movieId, reviews]) => {
                console.log('🔍 ReviewsTab: Processing movieId:', movieId, 'with reviews:', reviews);
                const movie = findMovieById(movieId);
                console.log('🔍 ReviewsTab: findMovieById result for', movieId, ':', movie);
                if (movie && reviews.length > 0) {
                  // Calculate aggregates
                  const totalOverall = reviews.reduce((sum, r) => sum + r.overallRating, 0);
                  const totalScariness = reviews.reduce((sum, r) => sum + r.scarinessRating, 0);
                  const avgOverall = totalOverall / reviews.length;
                  const avgScariness = totalScariness / reviews.length;
                  
                  // Get all unique tags
                  const allTags = new Set();
                  reviews.forEach(review => {
                    if (review.tags) {
                      review.tags.forEach(tag => allTags.add(tag));
                    }
                  });
                  
                  // Get all reviewers
                  const reviewers = reviews.map(r => r.name);
                  
                  const reviewedMovie = {
                    movie,
                    movieId: movieId, // Keep as string since TMDB IDs are strings
                    reviews,
                    avgOverall: Math.round(avgOverall * 10) / 10,
                    avgScariness: Math.round(avgScariness * 10) / 10,
                    reviewCount: reviews.length,
                    allTags: Array.from(allTags),
                    reviewers,
                    latestReview: reviews.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
                  };
                  console.log('🔍 ReviewsTab: Adding reviewed movie:', reviewedMovie);
                  reviewedMovies.push(reviewedMovie);
                } else {
                  console.log('🔍 ReviewsTab: Skipping movieId:', movieId, 'movie found:', !!movie, 'reviews count:', reviews?.length || 0);
                }
              });
              
              console.log('🔍 ReviewsTab: Final reviewedMovies array:', reviewedMovies);
              
              // Sort by latest review timestamp
              reviewedMovies.sort((a, b) => new Date(b.latestReview.timestamp) - new Date(a.latestReview.timestamp));

              if (reviewedMovies.length === 0) {
                return (
                  <div className="text-center py-12">
                    <h3 className="text-lg font-bold text-orange-300 mb-2">No Reviewed Movies Yet</h3>
                    <p className="text-orange-200">
                      Movies will appear here after viewers leave reviews for them.
                    </p>
                  </div>
                );
              }
              
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reviewedMovies.map(({ movie, reviews, avgOverall, avgScariness, reviewCount, allTags, reviewers }) => (
                    <div 
                      key={movie.id} 
                      className="bg-gray-700 border border-orange-500 rounded-lg overflow-hidden hover:border-orange-400 transition-colors cursor-pointer"
                      onClick={() => {
                        const fakeScheduledMovie = {
                          movie: movie,
                          date: new Date().toISOString().split('T')[0],
                          timeSlot: { id: 'review', time: 'Review View' },
                          slotsSpanned: 1
                        };
                        onMovieClick && onMovieClick(fakeScheduledMovie);
                      }}
                    >
                      {/* Movie Poster */}
                      <div className="relative">
                        <MoviePoster
                          movie={movie}
                          size="md"
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <h3 className="font-bold text-white text-lg">{movie.title}</h3>
                          <p className="text-gray-300 text-sm">{movie.year}</p>
                        </div>
                      </div>
                      
                      {/* Review Stats */}
                      <div className="p-4 space-y-3">
                        {/* Ratings */}
                        <div className="flex justify-between items-center">
                          <div className="text-center">
                            <div className="text-yellow-400 text-lg font-bold">⭐ {avgOverall}</div>
                            <div className="text-gray-400 text-xs">Overall</div>
                          </div>
                          <div className="text-center">
                            <div className="text-red-400 text-lg font-bold">👻 {avgScariness}</div>
                            <div className="text-gray-400 text-xs">Scary</div>
                          </div>
                          <div className="text-center">
                            <div className="text-blue-400 text-lg font-bold">{reviewCount}</div>
                            <div className="text-gray-400 text-xs">Reviews</div>
                          </div>
                        </div>
                        
                        {/* Reviewers */}
                        <div>
                          <div className="text-gray-400 text-xs mb-1">Reviewed by:</div>
                          <div className="text-orange-300 text-sm">
                            {reviewers.slice(0, 3).join(', ')}
                            {reviewers.length > 3 && ` +${reviewers.length - 3} more`}
                          </div>
                        </div>
                        
                        {/* Tags */}
                        {allTags.length > 0 && (
                          <div>
                            <div className="text-gray-400 text-xs mb-1">Tags:</div>
                            <div className="flex flex-wrap gap-1">
                              {allTags.slice(0, 4).map(tag => (
                                <span key={tag} className="bg-gray-600 text-orange-300 px-2 py-1 rounded text-xs">
                                  {tag}
                                </span>
                              ))}
                              {allTags.length > 4 && (
                                <span className="text-gray-400 text-xs">+{allTags.length - 4} more</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Streaming Service */}
                        {movie.preferredStreamingService && (() => {
                          const service = getStreamingServiceById(movie.preferredStreamingService);
                          return service ? (
                            <div className="flex items-center gap-2">
                              <StreamingServiceIcon 
                                service={service}
                                size="sm"
                                showName={false}
                              />
                              <span className="text-gray-400 text-xs">
                                {service.name}
                              </span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Hall of Horrors View */}
      {reviewsMode === 'hallOfInfamy' && (
        <div className="space-y-6">
          {/* Year Selector */}
          <div className="flex justify-center mb-6">
            <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-4 shadow-lg">
              <label className="block text-orange-300 text-sm font-bold mb-2">Filter by Year:</label>
              <select
                value={selectedHallYear}
                onChange={(e) => setSelectedHallYear(e.target.value)}
                className="bg-gray-700 border border-orange-500 rounded px-3 py-2 text-white"
              >
                <option value="all">All Time</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Best Movies */}
          <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-yellow-900 to-orange-900 px-4 py-3 border-b-2 border-orange-500">
              <h2 className="text-xl font-bold text-orange-300 flex items-center gap-2">
                🏆 Best Movies
              </h2>
              <p className="text-orange-200 text-sm">
                Top 5 highest rated movies {selectedHallYear !== 'all' ? `scheduled in ${selectedHallYear} calendar weeks` : 'of all time'} (3+ reviews required, weighted by review count)
              </p>
            </div>
            
            <div className="p-6">
              {(() => {
                const bestMovies = getHallOfInfamyMovies('best', selectedHallYear === 'all' ? null : selectedHallYear);
                
                if (bestMovies.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <h3 className="text-lg font-bold text-orange-300 mb-2">No Movies Eligible Yet</h3>
                      <p className="text-orange-200">
                        Movies need at least 3 reviews {selectedHallYear !== 'all' ? `and must have been scheduled in ${selectedHallYear} calendar weeks ` : ''}to appear in the Hall of Horrors
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    {bestMovies.map((movieStat, index) => (
                      <div 
                        key={movieStat.movie.id} 
                        className="bg-gray-700 border border-yellow-500 rounded-lg p-4 hover:border-yellow-400 transition-colors cursor-pointer"
                        onClick={() => {
                          const fakeScheduledMovie = {
                            movie: movieStat.movie,
                            date: new Date().toISOString().split('T')[0],
                            timeSlot: { id: 'hall', time: 'Hall of Horrors' },
                            slotsSpanned: 1
                          };
                          onMovieClick && onMovieClick(fakeScheduledMovie);
                        }}
                      >
                        <div className="flex items-center gap-4">
                          {/* Rank */}
                          <div className="text-center">
                            <div className="text-2xl font-bold text-yellow-400">
                              #{index + 1}
                            </div>
                            <div className="text-yellow-300 text-sm">
                              {movieStat.reviewCount} reviews
                            </div>
                          </div>
                          
                          {/* Movie Info */}
                          <div className="flex items-center gap-3 mb-3">
                            <MoviePoster
                              movie={movieStat.movie}
                              size="sm"
                              className="w-16 h-24 object-cover rounded border-2 border-yellow-600"
                            />
                            <div className="flex-1">
                              <h4 className="font-bold text-white text-lg">{movieStat.movie.title}</h4>
                              <p className="text-gray-300 text-sm">{movieStat.movie.year}</p>
                              
                              {/* Streaming Service */}
                              {movieStat.movie.preferredStreamingService && (
                                <div className="flex items-center gap-2 mt-1">
                                  <StreamingServiceIcon 
                                    service={getStreamingServiceById(movieStat.movie.preferredStreamingService)}
                                    size="sm"
                                    showName={false}
                                  />
                                  <span className="text-gray-400 text-xs">
                                    {getStreamingServiceById(movieStat.movie.preferredStreamingService)?.name}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Score */}
                          <div className="text-right">
                            <div className="text-2xl font-bold text-yellow-400">
                              ⭐ {movieStat.weightedOverall}
                            </div>
                            <div className="text-yellow-300 text-sm">
                              Weighted Score
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Scariest Movies */}
          <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-red-900 to-orange-900 px-4 py-3 border-b-2 border-orange-500">
              <h2 className="text-xl font-bold text-orange-300 flex items-center gap-2">
                👻 Scariest Movies
              </h2>
              <p className="text-orange-200 text-sm">
                Top 5 most terrifying movies {selectedHallYear !== 'all' ? `scheduled in ${selectedHallYear} calendar weeks` : 'of all time'} (3+ reviews required, weighted by review count)
              </p>
            </div>
            
            <div className="p-6">
              {(() => {
                const scariestMovies = getHallOfInfamyMovies('scariest', selectedHallYear === 'all' ? null : selectedHallYear);
                
                if (scariestMovies.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <h3 className="text-lg font-bold text-orange-300 mb-2">No Movies Eligible Yet</h3>
                      <p className="text-orange-200">
                        Movies need at least 3 reviews {selectedHallYear !== 'all' ? `and must have been scheduled in ${selectedHallYear} calendar weeks ` : ''}to appear in the Hall of Horrors
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    {scariestMovies.map((movieStat, index) => (
                      <div 
                        key={movieStat.movie.id} 
                        className="bg-gray-700 border border-red-500 rounded-lg p-4 hover:border-red-400 transition-colors cursor-pointer"
                        onClick={() => {
                          const fakeScheduledMovie = {
                            movie: movieStat.movie,
                            date: new Date().toISOString().split('T')[0],
                            timeSlot: { id: 'hall', time: 'Hall of Horrors' },
                            slotsSpanned: 1
                          };
                          onMovieClick && onMovieClick(fakeScheduledMovie);
                        }}
                      >
                        <div className="flex items-center gap-4">
                          {/* Rank */}
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-400">
                              #{index + 1}
                            </div>
                            <div className="text-red-300 text-sm">
                              {movieStat.reviewCount} reviews
                            </div>
                          </div>
                          
                          {/* Movie Info */}
                          <div className="flex items-center gap-3 mb-3">
                            <MoviePoster
                              movie={movieStat.movie}
                              size="sm"
                              className="w-16 h-24 object-cover rounded border-2 border-red-600"
                            />
                            <div className="flex-1">
                              <h4 className="font-bold text-white text-lg">{movieStat.movie.title}</h4>
                              <p className="text-gray-300 text-sm">{movieStat.movie.year}</p>
                              
                              {/* Streaming Service */}
                              {movieStat.movie.preferredStreamingService && (
                                <div className="flex items-center gap-2 mt-1">
                                  <StreamingServiceIcon 
                                    service={getStreamingServiceById(movieStat.movie.preferredStreamingService)}
                                    size="sm"
                                    showName={false}
                                  />
                                  <span className="text-gray-400 text-xs">
                                    {getStreamingServiceById(movieStat.movie.preferredStreamingService)?.name}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Score */}
                          <div className="text-right">
                            <div className="text-2xl font-bold text-red-400">
                              👻 {movieStat.weightedScariness}
                            </div>
                            <div className="text-red-300 text-sm">
                              Weighted Score
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReviewsTab;
